import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Brain, ArrowRight, CheckCircle, RotateCcw } from "lucide-react";

const DISTORTIONS = [
  { name: "All-or-Nothing Thinking", desc: "Seeing things in black and white with no middle ground.", example: '"If I\'m not perfect, I\'m a failure."' },
  { name: "Catastrophizing", desc: "Expecting the worst possible outcome.", example: '"This mistake will ruin everything."' },
  { name: "Mind Reading", desc: "Assuming you know what others are thinking.", example: '"They must think I\'m incompetent."' },
  { name: "Should Statements", desc: "Rigid rules about how things must be.", example: '"I should be able to handle this."' },
  { name: "Overgeneralization", desc: "Drawing broad conclusions from one event.", example: '"I always mess things up."' },
  { name: "Emotional Reasoning", desc: "Believing something is true because you feel it.", example: '"I feel anxious, so something bad must be happening."' },
];

const ThoughtReframe = () => {
  const [step, setStep] = useState(0);
  const [thought, setThought] = useState("");
  const [distortion, setDistortion] = useState(null);
  const [evidence, setEvidence] = useState("");
  const [reframe, setReframe] = useState("");

  const reset = () => { setStep(0); setThought(""); setDistortion(null); setEvidence(""); setReframe(""); };

  return (
    <div data-testid="thought-reframe" className="rounded-2xl p-8" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Thought Reframe</h2>
      <p className="text-sm mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
        CBT research shows that reframing negative thoughts reduces anxiety and improves mood. Let's walk through it together.
      </p>

      {/* Progress */}
      <div className="flex gap-1 mb-8">
        {["Catch", "Identify", "Challenge", "Reframe"].map((s, i) => (
          <div key={s} className="flex-1 text-center">
            <div className="h-1.5 rounded-full mb-1.5 transition-all" style={{ background: i <= step ? "var(--ll-green)" : "var(--ll-bg-secondary)" }} />
            <span className="text-[10px]" style={{ color: i <= step ? "var(--ll-text)" : "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{s}</span>
          </div>
        ))}
      </div>

      {/* Step 1: Catch */}
      {step === 0 && (
        <div className="space-y-4">
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>What negative thought is on your mind?</p>
          <Textarea data-testid="reframe-thought" value={thought} onChange={(e) => setThought(e.target.value)}
            placeholder="Write the thought exactly as it comes to you..." rows={3}
            className="rounded-xl border resize-none" style={{ borderColor: "var(--ll-border)" }} />
          <button disabled={!thought.trim()} onClick={() => setStep(1)}
            className="rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 transition-all"
            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
            Next <ArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Step 2: Identify distortion */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="p-3 rounded-xl mb-2" style={{ background: "var(--ll-bg-secondary)" }}>
            <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Your thought:</p>
            <p className="text-sm italic" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>"{thought}"</p>
          </div>
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Which thinking pattern does this sound like?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DISTORTIONS.map((d) => (
              <button key={d.name} onClick={() => { setDistortion(d); setStep(2); }}
                className="p-3 rounded-xl text-left transition-all hover:-translate-y-0.5"
                style={{ background: distortion?.name === d.name ? "rgba(163,184,153,0.2)" : "var(--ll-bg-secondary)", border: `1px solid ${distortion?.name === d.name ? "var(--ll-green)" : "transparent"}` }}>
                <p className="text-xs font-medium mb-0.5" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{d.name}</p>
                <p className="text-[10px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{d.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Challenge */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="p-3 rounded-xl" style={{ background: "rgba(247,208,70,0.08)" }}>
            <p className="text-xs font-medium" style={{ color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>{distortion?.name}</p>
            <p className="text-xs italic mt-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>"{thought}"</p>
          </div>
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>What evidence contradicts this thought?</p>
          <Textarea data-testid="reframe-evidence" value={evidence} onChange={(e) => setEvidence(e.target.value)}
            placeholder="Think of times when this wasn't true, facts that tell a different story..." rows={3}
            className="rounded-xl border resize-none" style={{ borderColor: "var(--ll-border)" }} />
          <button disabled={!evidence.trim()} onClick={() => setStep(3)}
            className="rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-40 transition-all"
            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
            Next <ArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Step 4: Reframe */}
      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Now, write a more balanced version of the thought:</p>
          <Textarea data-testid="reframe-new" value={reframe} onChange={(e) => setReframe(e.target.value)}
            placeholder="A kinder, more realistic way to see this..." rows={3}
            className="rounded-xl border resize-none" style={{ borderColor: "var(--ll-border)" }} />
          {reframe.trim() && (
            <div className="p-5 rounded-xl text-center" style={{ background: "rgba(163,184,153,0.1)" }}>
              <CheckCircle size={24} className="mx-auto mb-2" style={{ color: "var(--ll-green)" }} />
              <p className="text-xs mb-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Your reframed thought:</p>
              <p className="text-sm font-medium italic" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>"{reframe}"</p>
              <button onClick={reset} className="rounded-full px-5 py-2 text-xs font-medium flex items-center gap-1 mx-auto mt-4"
                style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                <RotateCcw size={12} /> Reframe Another Thought
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThoughtReframe;
